import React, { useMemo, useState } from "react";
import { Alert, CircularProgress, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SaveIcon from "@mui/icons-material/Save";
import * as XLSX from "xlsx";
import { CONTRACT_SIGNED_LABELS, IMAGE_AUTHORIZATION_LABELS, PAYMENT_LABELS, paymentReportRowsWithStatuses } from "./enrollmentPayment";

export function EnrollmentPaymentReport({ enrollments, students, contractStatuses, onLoadStudents, onSaveStatus, loading, error = "", updatedAt, onLoadLegacyStudents }) {
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState(null);
  const [filter, setFilter] = useState("all");
  const [contractFilter, setContractFilter] = useState("all");
  const [imageFilter, setImageFilter] = useState("all");
  const [drafts, setDrafts] = useState({});
  const [contractDrafts, setContractDrafts] = useState({});
  const [imageDrafts, setImageDrafts] = useState({});
  const [restrictionDrafts, setRestrictionDrafts] = useState({});
  const [savingId, setSavingId] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const rows = useMemo(() => paymentReportRowsWithStatuses(enrollments, students, contractStatuses), [enrollments, students, contractStatuses]);
  const visible = useMemo(() => rows.filter(row => {
    const paymentMatches = filter === "all" || (filter === "missing" ? !row.payment : row.payment === filter);
    const contractMatches =
      contractFilter === "all" ||
      (contractFilter === "signed" ? row.hasContractStatus && row.contractSigned :
        contractFilter === "unsigned" ? row.hasContractStatus && !row.contractSigned :
          !row.hasContractStatus);
    const imageMatches =
      imageFilter === "all" ||
      (imageFilter === "restricted" ? Boolean(row.imageRestrictions) :
        imageFilter === "missing" ? !row.imageAuthorization :
          row.imageAuthorization === imageFilter);
    return paymentMatches && contractMatches && imageMatches;
  }), [rows, filter, contractFilter, imageFilter]);
  const unloadedCount = useMemo(() => rows.filter(row => row.label === "Dados não carregados").length, [rows]);
  const effectivePage = Math.min(page, Math.max(0, Math.ceil(visible.length / rowsPerPage) - 1));
  const visiblePage = useMemo(() => visible.slice(effectivePage * rowsPerPage, effectivePage * rowsPerPage + rowsPerPage), [visible, effectivePage, rowsPerPage]);
  const updateFilter = (setter) => (event) => {
    setPage(0);
    setter(event.target.value);
  };
  const exportReport = () => {
    const sheet = XLSX.utils.aoa_to_sheet([["Nome do aluno matriculado", "Forma de pagamento", "Contrato", "Uso de imagem", "Restrição de imagem"], ...visible.map(row => [row.name, row.label, row.contractLabel, row.imageAuthorizationLabel, row.imageRestrictions || ""])]);
    sheet["!cols"] = [{ wch: 50 }, { wch: 24 }, { wch: 18 }, { wch: 20 }, { wch: 45 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Boleto e Carnê");
    XLSX.writeFile(workbook, "Alunos_Boleto_Carne.xlsx");
  };
  const saveStatus = async (row) => {
    const paymentValue = drafts[row.id] ?? row.payment;
    const contractValue = contractDrafts[row.id] ?? (row.hasContractStatus ? String(row.contractSigned) : "");
    const imageValue = imageDrafts[row.id] ?? row.imageAuthorization;
    const restrictionValue = restrictionDrafts[row.id] ?? row.imageRestrictions;
    const paymentChanged = paymentValue !== row.payment;
    const contractChanged = contractValue !== "" && (!row.hasContractStatus || contractValue !== String(row.contractSigned));
    const imageChanged = imageValue !== row.imageAuthorization;
    const restrictionChanged = restrictionValue !== row.imageRestrictions;
    if ((!paymentChanged && !contractChanged && !imageChanged && !restrictionChanged) || !onSaveStatus) return;
    setSavingId(row.id);
    try {
      setNotice(null);
      await onSaveStatus(row.id, {
        paymentOption: paymentValue || "",
        contractSigned: contractValue === "true",
        imageAuthorization: imageValue || "",
        imageRestrictions: restrictionValue || "",
        updatePayment: Boolean(paymentValue && paymentChanged),
        updateContract: contractChanged,
        updateImageAuthorization: imageChanged,
        updateImageRestrictions: restrictionChanged,
      });
      setNotice({ severity: "success", text: "Gravação confirmada." });
      setDrafts(prev => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
      setContractDrafts(prev => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
      setImageDrafts(prev => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
      setRestrictionDrafts(prev => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
    } catch (error) {
      setNotice({ severity: "error", text: error.code === "ENROLLMENT_STATUS_NOT_SAVED" ? error.message : "Não foi possível confirmar a gravação. Sua alteração continua na tela para nova tentativa. Atualize o relatório para conferir os dados salvos." });
    } finally {
      setSavingId("");
    }
  };
  return <>
    <Button variant="outlined" startIcon={<ReceiptLongIcon />} onClick={() => { setOpen(true); setNotice(null); onLoadStudents?.(); }}>
      Boleto/Carnê
    </Button>
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="lg" PaperProps={{ sx: { m: { xs: 1, sm: 4 }, width: { xs: "calc(100% - 16px)", sm: "calc(100% - 64px)" } } }}>
      <DialogTitle>Relatório de boleto e carnê</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2, alignItems: "center" }}>
          <Button onClick={() => { setNotice(null); onLoadStudents?.(); }} disabled={loading || Boolean(savingId)}>Atualizar opções</Button>
          {onLoadLegacyStudents && <Button onClick={onLoadLegacyStudents} disabled={loading || Boolean(savingId)}>Buscar dados antigos</Button>}
          <TextField select size="small" label="Forma de pagamento" value={filter} onChange={updateFilter(setFilter)} sx={{ minWidth: 210 }}>
            <MenuItem value="all">Todas</MenuItem>
            <MenuItem value="boleto">Boleto</MenuItem>
            <MenuItem value="carne">Carnê</MenuItem>
            <MenuItem value="missing">Sem opção informada</MenuItem>
          </TextField>
          <TextField select size="small" label="Contrato" value={contractFilter} onChange={updateFilter(setContractFilter)} sx={{ minWidth: 190 }}>
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="signed">Assinado</MenuItem>
            <MenuItem value="unsigned">Não assinado</MenuItem>
            <MenuItem value="missing">Não informado</MenuItem>
          </TextField>
          <TextField select size="small" label="Uso de imagem" value={imageFilter} onChange={updateFilter(setImageFilter)} sx={{ minWidth: 210 }}>
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="sim">Autorizado</MenuItem>
            <MenuItem value="nao">Não autorizado</MenuItem>
            <MenuItem value="restricted">Com restrição</MenuItem>
            <MenuItem value="missing">Não informado</MenuItem>
          </TextField>
          <Button onClick={exportReport} disabled={loading || Boolean(error) || Boolean(savingId) || !visible.length || unloadedCount > 0}>Exportar boleto/carnê</Button>
          <Typography variant="body2" color="text.secondary">{visible.length} de {rows.length} aluno(s)</Typography>
        </Box>
        {loading && <Box display="flex" alignItems="center" gap={1} mb={1} role="status"><CircularProgress size={18} /><Typography variant="body2">Consultando dados. Aguarde a conclusão antes de salvar ou exportar.</Typography></Box>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {notice && <Alert severity={notice.severity} sx={{ mb: 2 }}>{notice.text}</Alert>}
        {updatedAt && <Typography variant="caption" color="text.secondary" display="block" mb={1}>Última consulta confirmada: {new Date(updatedAt).toLocaleTimeString('pt-BR')}</Typography>}
        <Typography variant="caption" color="text.secondary" display="block" mb={2}>Atualizar opções consulta os dados salvos. Para registros anteriores sem informação, consulte as observações antigas; essa busca pode levar mais tempo. Altere os campos e clique em Salvar em cada linha.</Typography>
        {unloadedCount > 0 && <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>Atualize as opções antes de exportar ou salvar alterações.</Typography>}
        <TableContainer sx={{ maxHeight: 460 }}><Table stickyHeader size="small" aria-label="Relatório de boleto e carnê">
          <TableHead><TableRow><TableCell sx={{ minWidth: 180 }}>Nome do aluno matriculado</TableCell><TableCell>Forma de pagamento</TableCell><TableCell>Contrato</TableCell><TableCell>Uso de imagem</TableCell><TableCell>Restrição de imagem</TableCell><TableCell align="right" sx={{ right: 0, zIndex: 3, bgcolor: "background.paper" }}>Salvar</TableCell></TableRow></TableHead>
          <TableBody>{!visiblePage.length && <TableRow><TableCell colSpan={6}>{loading ? "Carregando informações…" : rows.length ? "Nenhum aluno corresponde aos filtros selecionados. Escolha Todas para conferir os registros sem opção informada." : "Busque as matrículas para consultar este relatório."}</TableCell></TableRow>}{visiblePage.map(row => {
            const value = drafts[row.id] ?? row.payment;
            const contractValue = contractDrafts[row.id] ?? (row.hasContractStatus ? String(row.contractSigned) : "");
            const imageValue = imageDrafts[row.id] ?? row.imageAuthorization;
            const restrictionValue = restrictionDrafts[row.id] ?? row.imageRestrictions;
            const paymentChanged = value !== row.payment;
            const contractChanged = contractValue !== "" && (!row.hasContractStatus || contractValue !== String(row.contractSigned));
            const imageChanged = imageValue !== row.imageAuthorization;
            const restrictionChanged = restrictionValue !== row.imageRestrictions;
            const canSave = Boolean((paymentChanged || contractChanged || imageChanged || restrictionChanged) && onSaveStatus && !loading && !error && !savingId && (value || !paymentChanged));
            return <TableRow key={row.id}>
              <TableCell>{row.name}</TableCell>
              <TableCell>
                <TextField select size="small" SelectProps={{ displayEmpty: true }} inputProps={{ "aria-label": `Forma de pagamento de ${row.name}` }} value={value || ""} onChange={e => setDrafts(prev => ({ ...prev, [row.id]: e.target.value }))} sx={{ minWidth: 150 }} disabled={loading || Boolean(savingId) || Boolean(error) || row.label === "Dados não carregados"}>
                  <MenuItem value="">Não informado</MenuItem>
                  {Object.entries(PAYMENT_LABELS).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
                </TextField>
              </TableCell>
              <TableCell>
                <TextField select size="small" SelectProps={{ displayEmpty: true }} inputProps={{ "aria-label": `Contrato de ${row.name}` }} value={contractValue} disabled={loading || Boolean(savingId) || Boolean(error)} onChange={e => setContractDrafts(prev => ({ ...prev, [row.id]: e.target.value }))} sx={{ minWidth: 150 }}>
                  <MenuItem value="">Não informado</MenuItem>
                  {Object.entries(CONTRACT_SIGNED_LABELS).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
                </TextField>
              </TableCell>
              <TableCell>
                <TextField select size="small" SelectProps={{ displayEmpty: true }} inputProps={{ "aria-label": `Uso de imagem de ${row.name}` }} value={imageValue || ""} onChange={e => setImageDrafts(prev => ({ ...prev, [row.id]: e.target.value }))} sx={{ minWidth: 170 }} disabled={loading || Boolean(savingId) || Boolean(error) || row.label === "Dados não carregados"}>
                  <MenuItem value="">Não informado</MenuItem>
                  {Object.entries(IMAGE_AUTHORIZATION_LABELS).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
                </TextField>
              </TableCell>
              <TableCell>
                <TextField size="small" inputProps={{ "aria-label": `Restrição de imagem de ${row.name}` }} value={restrictionValue || ""} onChange={e => setRestrictionDrafts(prev => ({ ...prev, [row.id]: e.target.value }))} sx={{ minWidth: 220 }} disabled={loading || Boolean(savingId) || Boolean(error) || row.label === "Dados não carregados"} />
              </TableCell>
              <TableCell align="right" sx={{ position: "sticky", right: 0, zIndex: 1, bgcolor: "background.paper" }}>
                <Tooltip title={canSave ? "Salvar opção" : "Sem alteração para salvar"}>
                  <span><IconButton size="small" onClick={() => saveStatus(row)} disabled={!canSave || savingId === row.id} aria-label={`Salvar status de matrícula de ${row.name}`}><SaveIcon fontSize="small" /></IconButton></span>
                </Tooltip>
              </TableCell>
            </TableRow>;
          })}</TableBody>
	        </Table></TableContainer>
        <TablePagination
          component="div"
          count={visible.length}
          page={effectivePage}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage="Linhas por página"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </DialogContent>
      <DialogActions><Button onClick={() => setOpen(false)}>Fechar</Button></DialogActions>
    </Dialog>
  </>;
}
